import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import {
  ClipboardList,
  CalendarHeart,
  CalendarCheck,
  Clock3,
  Sparkles,
  Share2,
  SearchX,
} from 'lucide-react'
import { useAppDispatch, useAppSelector } from '../../../Redux/Hooks'
import { getUserCreatedEventsAPI } from '../../../Redux/ApiCalls/Dashboard/EventAPI'
import { UserCreatedEvents as UserCreatedEventsType } from '../../../Data/event.dto'
import UserCreatedEventCard from '../../atoms/Dashboard/Events/UserCreatedEventCard'
import ShareEventFormLinkPopup from '../../atoms/Dashboard/Navbar/ShareFormLinkPopup'
import MarketplaceEmptyState, { MarketplaceLoadingSpinner } from './Marketplace/MarketplaceEmptyState'
import { device } from '../../atoms/Utlis/media'
import { getAddedToCalendarIds } from '../../../Utils/inquiryCalendarStorage'

type InquiryFilter = 'all' | 'pending' | 'in_calendar'

const UserCreatedEvents: React.FC = () => {
  const { userCreatedEvents, loading } = useAppSelector((state) => state.event)
  const { selectedBranch, user } = useAppSelector((state) => state.auth)
  const userId = user?.id
  const dispatch = useAppDispatch()
  const [currentEvents, setCurrentEvents] = useState<UserCreatedEventsType[]>([])
  const [isShareFormPopUp, setIsShareFormPopUp] = useState<boolean>(false)
  const [filter, setFilter] = useState<InquiryFilter>('all')
  const [calendarStatusVersion, setCalendarStatusVersion] = useState(0)

  useEffect(() => {
    dispatch(getUserCreatedEventsAPI({ branch_id: selectedBranch?.id }))
  }, [dispatch, selectedBranch?.id])

  useEffect(() => {
    if (userCreatedEvents) {
      setCurrentEvents(userCreatedEvents)
    }
  }, [userCreatedEvents])

  const addedIds = useMemo(() => {
    void calendarStatusVersion
    return new Set(getAddedToCalendarIds(userId))
  }, [userId, calendarStatusVersion, currentEvents])

  const pendingCount = currentEvents.filter((e) => !addedIds.has(e.id)).length
  const inCalendarCount = currentEvents.filter((e) => addedIds.has(e.id)).length

  const filteredEvents = useMemo(() => {
    if (filter === 'pending') {
      return currentEvents.filter((e) => !addedIds.has(e.id))
    }
    if (filter === 'in_calendar') {
      return currentEvents.filter((e) => addedIds.has(e.id))
    }
    return currentEvents
  }, [currentEvents, filter, addedIds])

  const totalInquiries = userCreatedEvents?.length ?? 0
  const subEventCount =
    userCreatedEvents?.reduce((sum, e) => sum + (e.sub_events?.length ?? 0), 0) ?? 0

  const filterTabs: {
    key: InquiryFilter
    label: string
    count: number
    tone: string
    icon: React.ReactNode
  }[] = [
      {
        key: 'all',
        label: 'Total inquiries',
        count: totalInquiries,
        tone: '#c94daa',
        icon: <ClipboardList size={16} />,
      },
      {
        key: 'pending',
        label: 'Pending (Unscheduled)',
        count: pendingCount,
        tone: '#f09a3e',
        icon: <Clock3 size={16} />,
      },
      {
        key: 'in_calendar',
        label: 'In calendar',
        count: inCalendarCount,
        tone: '#35b7c8',
        icon: <CalendarCheck size={16} />,
      },
    ]

  const emptyFilterCopy =
    filter === 'pending'
      ? {
        title: 'No pending inquiries',
        description: 'All event inquiries have already been added to your calendar.',
      }
      : filter === 'in_calendar'
        ? {
          title: 'No inquiries in calendar',
          description: 'Add an inquiry to your calendar to see it listed here.',
        }
        : {
          title: 'No event inquiries yet',
          description:
            'Share your event inquiry form with customers — responses will appear here for you to review and follow up.',
        }

  return (
    <PageContainer>
      {isShareFormPopUp && (
        <ShareEventFormLinkPopup onClose={() => setIsShareFormPopUp(false)} />
      )}

      <Hero>
        <HeroGlow />
        <HeroContent>
          <HeroText>
            <div className="flex flex-col">
              <HeroBadge>
                <Sparkles size={13} />
                Customer inquiries
              </HeroBadge>
              <PageTitle className="linear-text">Event Inquiries</PageTitle>
              <PageSubtitle>
                View and manage event inquiries submitted by customers through your shared form.
              </PageSubtitle>
            </div>
          </HeroText>
          <ShareButton type="button" onClick={() => setIsShareFormPopUp(true)}>
            <Share2 size={16} strokeWidth={2.4} />
            Share form
          </ShareButton>
        </HeroContent>
      </Hero>

      {!(loading && currentEvents.length === 0) && (
        <StatsGrid>
          {filterTabs.map((tab) => {
            const active = filter === tab.key
            return (
              <FilterStatCard
                key={tab.key}
                type="button"
                $active={active}
                $tone={tab.tone}
                $featured={tab.key === 'all'}
                onClick={() => setFilter(tab.key)}
                aria-pressed={active}
              >
                <StatIcon $tone={tab.tone} $active={active} $featured={tab.key === 'all'}>
                  {tab.icon}
                </StatIcon>
                <StatBody>
                  <StatValue $active={active} $featured={tab.key === 'all'}>
                    {tab.count}
                  </StatValue>
                  <StatLabel $active={active} $featured={tab.key === 'all'}>
                    {tab.label}
                  </StatLabel>
                </StatBody>
              </FilterStatCard>
            )
          })}

          <InfoStatCard>
            <StatIcon $tone="#35b7c8">
              <CalendarHeart size={16} />
            </StatIcon>
            <StatBody>
              <StatValue>{subEventCount}</StatValue>
              <StatLabel>Sub-events listed</StatLabel>
            </StatBody>
          </InfoStatCard>
        </StatsGrid>
      )}

      <Panel>
        {loading && currentEvents.length === 0 ? (
          <MarketplaceLoadingSpinner padding="60px 22px" />
        ) : filteredEvents.length > 0 ? (
          <CardsGrid>
            {filteredEvents.map((event) => (
              <UserCreatedEventCard
                key={event.id}
                userEvent={event}
                onCalendarStatusChange={() => setCalendarStatusVersion((v) => v + 1)}
              />
            ))}
          </CardsGrid>
        ) : (
          <MarketplaceEmptyState
            padding="48px 22px"
            icon={
              filter !== 'all' ? (
                <SearchX size={36} strokeWidth={1.5} />
              ) : (
                <CalendarHeart size={36} strokeWidth={1.5} />
              )
            }
            title={emptyFilterCopy.title}
            description={emptyFilterCopy.description}
            actions={
              filter !== 'all'
                ? [
                  {
                    label: 'Show all inquiries',
                    onClick: () => setFilter('all'),
                    variant: 'secondary' as const,
                  },
                ]
                : [
                  {
                    label: 'Share Form',
                    onClick: () => setIsShareFormPopUp(true),
                    icon: <Share2 size={15} />,
                  },
                ]
            }
            hint={
              filter === 'all'
                ? {
                  icon: <Sparkles size={13} />,
                  text: 'Collect event details, dates & customer contact info',
                }
                : undefined
            }
          />
        )}
      </Panel>
    </PageContainer>
  )
}

export default UserCreatedEvents

const gradient =
  'linear-gradient(144.01deg, rgba(255, 201, 81, 0.8) 0%, rgba(206, 86, 176, 0.8) 100%)'

const PageContainer = styled.div`
  width: 100%;
  padding: 24px;
  font-family: Urbanist, sans-serif;
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex-shrink: 0;
  box-sizing: border-box;
  @media ${device.tablet} {
    padding: 16px;
  }
`

const Hero = styled.div`
  position: relative;
  overflow: visible;
  flex-shrink: 0;
  border-radius: 20px;
  padding: 24px 26px;
  background: linear-gradient(
    135deg,
    rgba(255, 201, 81, 0.16) 0%,
    rgba(206, 86, 176, 0.12) 55%,
    rgba(53, 183, 200, 0.08) 100%
  );
  border: 1px solid rgba(201, 77, 170, 0.12);
  box-shadow: 0 8px 28px rgba(201, 77, 170, 0.08);
`

const HeroGlow = styled.div`
  pointer-events: none;
  position: absolute;
  top: -80px;
  right: -4%;
  width: 240px;
  height: 240px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 201, 81, 0.32) 0%, transparent 70%);
`

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`

const HeroText = styled.div`
  min-width: 0;
`

const HeroBadge = styled.span`
  display: inline-flex;
  width: fit-content;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
  padding: 5px 11px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(201, 77, 170, 0.14);
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: #c94daa;
`

const PageTitle = styled.h1`
  margin: 0;
  font-size: 28px;
  font-weight: 800;
  line-height: 1.25;
  padding: 2px 0;
`

const PageSubtitle = styled.p`
  margin: 0px 0 0;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.62);
  max-width: 560px;
`

const ShareButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 42px;
  padding: 0 18px;
  border: none;
  border-radius: 12px;
  background: ${gradient};
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 8px 18px rgba(201, 77, 170, 0.22);
  white-space: nowrap;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 10px 22px rgba(201, 77, 170, 0.28);
  }
`

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  flex-shrink: 0;
  @media (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`

const FilterStatCard = styled.button<{
  $active: boolean
  $tone: string
  $featured?: boolean
}>`
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 16px;
  text-align: left;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
  border: 1px solid
    ${(p) =>
    p.$active
      ? p.$featured
        ? 'transparent'
        : `${p.$tone}55`
      : 'rgba(23, 23, 23, 0.06)'};
  background: ${(p) =>
    p.$active && p.$featured
      ? gradient
      : p.$active
        ? `${p.$tone}14`
        : '#fff'};
  box-shadow: ${(p) =>
    p.$active && p.$featured
      ? '0 8px 22px rgba(201, 77, 170, 0.22)'
      : p.$active
        ? `0 6px 18px ${p.$tone}22`
        : '0 6px 18px rgba(23, 23, 23, 0.04)'};
  color: ${(p) => (p.$active && p.$featured ? '#fff' : '#171717')};

  &::after {
    content: '';
    position: absolute;
    right: -20px;
    bottom: -24px;
    width: 90px;
    height: 90px;
    border-radius: 50%;
    background: ${(p) =>
    p.$active && p.$featured ? 'rgba(255, 255, 255, 0.12)' : 'transparent'};
  }

  &:hover {
    transform: translateY(-1px);
    border-color: ${(p) =>
    p.$active && p.$featured ? 'transparent' : `${p.$tone}40`};
  }
`

const InfoStatCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 16px;
  background: #fff;
  border: 1px solid rgba(23, 23, 23, 0.06);
  box-shadow: 0 6px 18px rgba(23, 23, 23, 0.04);
`

const StatIcon = styled.div<{
  $tone: string
  $active?: boolean
  $featured?: boolean
}>`
  position: relative;
  z-index: 1;
  width: 36px;
  height: 36px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${(p) => (p.$active && p.$featured ? '#fff' : p.$tone)};
  background: ${(p) =>
    p.$active && p.$featured ? 'rgba(255, 255, 255, 0.22)' : `${p.$tone}18`};
`

const StatBody = styled.div`
  position: relative;
  z-index: 1;
  min-width: 0;
`

const StatValue = styled.div<{ $active?: boolean; $featured?: boolean }>`
  font-family: Urbanist, sans-serif;
  font-size: 20px;
  font-weight: 800;
  color: ${(p) => (p.$active && p.$featured ? '#fff' : '#171717')};
  line-height: 1.1;
`

const StatLabel = styled.div<{ $active?: boolean; $featured?: boolean }>`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: ${(p) =>
    p.$active && p.$featured ? 'rgba(255, 255, 255, 0.88)' : 'rgba(23, 23, 23, 0.5)'};
  margin-top: 2px;
`

const Panel = styled.div`
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: #ffffff;
  border: 1px solid rgba(23, 23, 23, 0.06);
  border-radius: 20px;
  box-shadow: 0 10px 32px rgba(23, 23, 23, 0.06);
  overflow: hidden;
  flex-shrink: 0;
`

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(500px, 1fr));
  gap: 20px;
  padding: 22px;
  overflow-y: auto;
  align-content: start;
  @media ${device.tablet} {
    grid-template-columns: 1fr;
    padding: 16px;
  }
`
